import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  inject,
} from '@angular/core';
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
} from '@ng-bootstrap/ng-bootstrap';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-date1',
  templateUrl: './date1.component.html',
  styleUrls: ['./date1.component.css'],
  standalone: false,
})
export class Date1Component implements OnChanges, DoCheck {
  @Input() mode: 'range' | 'single' = 'range';
  @Output() dateChange = new EventEmitter<{
    from: NgbDate | null;
    to: NgbDate | null;
  }>();

  @Input() from: NgbDate | null = null;
  @Input() to: NgbDate | null = null;

  value = '';
  navigation = 'arrows';
  outsideDays = 'visible';
  calendar = inject(NgbCalendar);
  formatter = inject(NgbDateParserFormatter);
  translate = inject(TranslateService);

  hoveredDate: NgbDate | null = null;
  fromDate: NgbDate | null = null;
  toDate: NgbDate | null = null;

  onDateSelection(date: NgbDate) {
    if (this.mode === 'single') {
      this.fromDate = date;
      this.toDate = null;
      this.emitDateChange();
      return;
    }

    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (
      this.fromDate &&
      !this.toDate &&
      date &&
      date.after(this.fromDate)
    ) {
      this.toDate = date;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
    this.emitDateChange();
  }

  ngOnChanges(): void {
    if (this.mode === 'single') {
      this.fromDate = this.from ?? null;
      this.toDate = null;
    } else {
      this.fromDate = this.from ?? null;
      this.toDate = this.to ?? null;
    }

    this.emitDateChange();
  }

  isHovered(date: NgbDate) {
    if (this.mode === 'single') return false;
    return (
      this.fromDate &&
      !this.toDate &&
      this.hoveredDate &&
      date.after(this.fromDate) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    if (this.mode === 'single') return false;
    return (
      this.fromDate &&
      this.toDate &&
      date.after(this.fromDate) &&
      date.before(this.toDate)
    );
  }

  isRange(date: NgbDate) {
    if (this.mode === 'single') {
      return this.fromDate ? date.equals(this.fromDate) : false;
    }
    return (
      (this.fromDate && date.equals(this.fromDate)) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
    const parsed = this.formatter.parse(input);
    const next =
      parsed && this.calendar.isValid(NgbDate.from(parsed))
        ? NgbDate.from(parsed)
        : currentValue;

    if (this.mode === 'single') {
      this.fromDate = next;
      this.toDate = null;
      this.emitDateChange();
      return next;
    }

    return next;
  }

  ngDoCheck() {
    if (!this.fromDate) {
      return;
    }

    if (this.mode === 'single') {
      this.value = this.formatter.format(this.fromDate);
    } else {
      this.value =
        this.formatter.format(this.fromDate) +
        ' - ' +
        (this.toDate ? this.formatter.format(this.toDate) : '');
    }
  }

  private emitDateChange() {
    this.dateChange.emit({
      from: this.fromDate,
      to: this.mode === 'single' ? null : this.toDate,
    });
  }

  reset(): void {
    this.fromDate = null;
    this.toDate = null;
    this.value = '';
    this.emitDateChange();
  }
}
